import axios from "axios";
import * as Tabs from "@radix-ui/react-tabs";
import { useUpdateAtom } from "jotai/utils";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "../components/Button";
import Icon from "../components/Icon";
import { DEFAULT_GRID_RESOLUTION } from "../consts";
import { blockYouJustCreatedAtom } from "../jotai/atoms";
import { styled } from "../stitches.config";
import { AnyBlock, ImageSearchBlock as ImageSearchBlockType } from "../types";
import { pixelToGridSize } from "../utils/gridPosition";
import { BlockInput } from "./WebBlock";

export default function ImageSearchBlock(props: {
  block: ImageSearchBlockType;
  justCreated: boolean;
  updateBlock: (block: Partial<AnyBlock>) => void;
}) {
  const { id, searchType } = props.block;

  function onTabChange(tab: string) {
    let searchType = tab as ImageSearchBlockType["searchType"];
    if (tab === "img") {
      searchType = "transparent";
    }
    if (tab === "gif") {
      searchType = "gif-transparent";
    }

    props.updateBlock({
      id,
      searchType,
      value: "",
      offset: 0,
    });
  }

  return (
    <Tabs.Root
      value={tabValue(searchType)}
      orientation="vertical"
      onValueChange={onTabChange}
    >
      <TabControl aria-label="select image type">
        <TabTrigger value="gif" data-cursor={"pointer"}>
          GIF
        </TabTrigger>
        <TabTrigger value="img" data-cursor={"pointer"}>
          Image
        </TabTrigger>
        <TabTrigger value="gif-text" data-cursor={"pointer"}>
          Text
        </TabTrigger>
      </TabControl>
      <TabContent value="gif">
        <ImageSearch {...props} />
      </TabContent>
      <TabContent value="img">
        <ImageSearch {...props} />
      </TabContent>
      <TabContent value="gif-text">
        <ImageSearch {...props} />
      </TabContent>
    </Tabs.Root>
  );
}

type Tab = "img" | "gif" | "gif-text";

function tabValue(searchType: ImageSearchBlockType["searchType"]): Tab {
  switch (searchType) {
    case "":
      return "img";
    case "transparent":
      return "img";
    case "gif":
      return "gif";
    case "gif-transparent":
      return "gif";
    case "gif-text":
      return "gif-text";
  }
}

const MAX_RESULT = 25;

function makeQuery(term: string, offset: number, type: string) {
  return (
    "?q=" +
    encodeURIComponent(term) +
    `&count=${MAX_RESULT}` +
    `&offset=${offset}` +
    (type ? `&imageType=${type}` : "") +
    "&maxFileSize=1500000" //1.5MB
  );
}

type SearchResultItem = {
  contentUrl: string;
  thumbnailUrl: string;
  name: string;
  width: number;
  height: number;
};

function ImageSearch({
  block,
  updateBlock,
  justCreated,
}: {
  block: ImageSearchBlockType;
  justCreated: boolean;
  updateBlock: (block: Partial<AnyBlock>) => void;
}) {
  const [isFetching, setIsFetching] = useState(false);
  const textInputRef = useRef<HTMLInputElement>(null);
  const text = block.text;

  const data: SearchResultItem[] = useMemo(() => {
    if (!block.value) {
      return [];
    }
    try {
      const data = JSON.parse(block.value);
      return data as SearchResultItem[];
    } catch (e) {
      console.error(e);
    }
    return [];
  }, [block]);

  const offset = block.offset || 0;

  const minBlockSize = 4;
  let gridCol = Math.min(Math.round(block.size.width / minBlockSize), 6);
  let gridRow = Math.min(Math.round((block.size.height - 3) / minBlockSize), 6);

  if (gridCol * gridRow > MAX_RESULT) {
    gridCol = 5;
    gridRow = 5;
  }

  const fetchData = useCallback(
    async (fresh: boolean) => {
      if (text === undefined) {
        return;
      }
      const effectiveOffset = fresh ? 0 : offset;
      setIsFetching(true);
      const result = await axios({
        method: "GET",
        url:
          "/api/imagesearch" +
          makeQuery(text, effectiveOffset, block.searchType),
      });
      if (result.data.value.length) {
        updateBlock({
          offset: effectiveOffset + gridCol * gridRow,
          value: JSON.stringify(
            result.data.value
              .slice(0, MAX_RESULT)
              .map(
                ({
                  contentUrl,
                  thumbnailUrl,
                  name,
                  width,
                  height,
                }: {
                  contentUrl: string;
                  thumbnailUrl: string;
                  name: string;
                  width: string;
                  height: string;
                }) => {
                  return { contentUrl, thumbnailUrl, name, width, height };
                }
              )
          ),
        });
        setIsFetching(false);
      }
    },
    [text, offset, block.searchType, updateBlock, gridCol, gridRow]
  );

  useEffect(() => {
    if (!data?.length && !isFetching) {
      fetchData(true);
    }
  }, [data, isFetching, fetchData]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    e.stopPropagation(); // prevent canvas key commands from conflicting

    if (!textInputRef) return;

    switch (e.key) {
      case "Escape":
        textInputRef.current?.blur();
        break;
      case "Enter":
        fetchData(true);
        textInputRef.current?.blur();
        break;
      default:
    }
  }

  const setBlockYouJustCreated = useUpdateAtom(blockYouJustCreatedAtom);

  useEffect(() => {
    if (textInputRef && justCreated) {
      textInputRef.current?.focus();
      setBlockYouJustCreated(undefined);
    }
  }, [justCreated, textInputRef, setBlockYouJustCreated]);

  const contentHeight =
    block.size.height * DEFAULT_GRID_RESOLUTION -
    CONTROL_HEIGHT -
    TAB_HEIGHT -
    10; // "10" is manual adjustment back to make CSS work...

  return (
    <BrowserContainer>
      <SearchControls>
        <InputBox>
          <BlockInput
            type="text"
            value={text}
            onMouseDown={(e) => e.stopPropagation()} // prevent moving/selecting block
            onChange={(event) => {
              updateBlock({
                text: event.target.value,
              });
            }}
            data-cursor={"text"}
            ref={textInputRef}
            onKeyDown={onKeyDown}
          />
          {block.searchType === "gif-text" ? null : (
            <TypesButton
              marginOff
              title="Transparency"
              onClick={() => {
                updateBlock({
                  searchType: toggleTransparent(block.searchType),
                  value: "",
                  offset: 0,
                });
              }}
            >
              <Icon
                icon="Transparent"
                color={
                  block.searchType.includes("transparent") ? undefined : "white"
                }
              />
            </TypesButton>
          )}
        </InputBox>
        <GoButton
          marginOff
          title="Search"
          onClick={() => {
            fetchData(true);
          }}
        >
          <Icon icon="Search" />
        </GoButton>
      </SearchControls>
      <ResultsContainer
        style={{
          gridTemplateColumns: `repeat( ${gridCol}, 1fr)`,
          gridTemplateRows: `repeat(${gridRow}, 1fr)`,
        }}
      >
        {data.slice(0, gridCol * gridRow)?.map((item) => (
          <Img
            style={{ maxHeight: contentHeight / gridRow }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            data-cursor={"grab"}
            blend={block.searchType === "transparent"}
            onDragStart={(e: React.DragEvent<HTMLImageElement>) => {
              const dTarget = e.target as HTMLImageElement;
              const { width, height } = dTarget.getBoundingClientRect();
              const aspectRatio = item.height / item.width;
              const newHeight = width * aspectRatio;
              const newWidth = height / aspectRatio;
              if (newHeight < height) {
                dTarget.style.padding = `${(height - newHeight) / 2}px 0 `;
              } else {
                dTarget.style.padding = ` 0 ${(width - newWidth) / 2}px  `;
              }
              e.dataTransfer.setData(
                "text/plain",
                JSON.stringify({
                  url: item.contentUrl,
                  width: item.width,
                  height: item.height,
                })
              );
            }}
            onDoubleClick={() => {
              const aspectRatio = item.width / item.height;
              const gridSize = pixelToGridSize({
                width: item.width,
                height: item.height,
              });
              gridSize.width = Math.min(block.size.width, gridSize.width);
              gridSize.height = gridSize.width / aspectRatio;
              const blockData: Partial<AnyBlock> = {
                type: "image",
                value: item.contentUrl,
                size: gridSize,
              };
              updateBlock(blockData);
              setTimeout(() => updateBlock(blockData), 100);
            }}
            src={item.thumbnailUrl}
            key={item.contentUrl}
            alt={item.name}
          />
        ))}
      </ResultsContainer>
      <MoreButton
        icon="Plus"
        color="secondary"
        onClick={() => {
          fetchData(false);
        }}
      ></MoreButton>
    </BrowserContainer>
  );
}

function toggleTransparent(
  searchType: ImageSearchBlockType["searchType"]
): ImageSearchBlockType["searchType"] {
  switch (searchType) {
    case "":
      return "transparent";
    case "gif":
      return "gif-transparent";
    case "transparent":
      return "";
    case "gif-transparent":
      return "gif";
    case "gif-text":
      return "gif-text";
  }
}

export function defaultSearch() {
  const terms = [
    "carrot",
    "campfire",
    "vines",
    "butterfly",
    "wings",
    "horse",
    "hat",
    "moss",
    "pearl",
    "snail",
    "elephant",
    "frog",
    "maple",
    "quartz",
    "cloud",
    "mushroom",
    "flower",
    "salt lamp",
    "anteater",
    "apple",
    "ant",
    "pokemon hat",
    "kawaii stickers",
    "patapon",
    "volleyball",
    "succulent",
    "airplants",
    "coffee",
  ];
  return terms[Math.floor(Math.random() * terms.length)];
}

const CONTROL_HEIGHT = 48;
const TAB_HEIGHT = 40;

const TabControl = styled(Tabs.List, {
  height: `${TAB_HEIGHT}px`,
});

const TabContent = styled(Tabs.Content, {
  "&:focus": {
    outline: "none",
  },
});

const TabTrigger = styled(Tabs.Trigger, {
  // button style reset
  border: "none",
  width: "auto",
  overflow: "visible",
  font: "inherit",
  lineHeight: "normal",
  ["-webkit-font-smoothing"]: "inherit",
  ["-moz-osx-font-smoothing"]: "inherit",
  ["-webkit-appearance"]: "none",

  margin: "$1",
  padding: "$buttonPadding $2",
  background: "transparent",
  color: "$paper10",
  borderRadius: "$2",
  cursor: "pointer",
  fontSize: "$4",
  display: "inline-flex",
  placeContent: "center",
  fontFamily: "$default",
  "&:hover": {
    background: "$transparentBlack05",
  },
  "&[data-state=active]": {
    background: "$transparentBlack05",
    fontWeight: "500",
  },
});

const InputBox = styled("div", {
  position: "relative",
});

const MoreButton = styled(Button, {
  position: "absolute",
  right: 0,
  bottom: 0,
  border: "1px solid $transparentBrown40",
});

// Fixes bug with <img> object-fit not working well in grid-template
const ResultsContainer = styled("div", {
  width: "100%",
  display: "grid",
  overflow: "hidden",
  backgroundColor: "$beige",
  height: `calc(100% - ${TAB_HEIGHT + CONTROL_HEIGHT}px)`,
  position: "absolute",
  inset: `${TAB_HEIGHT + CONTROL_HEIGHT}px 0px 0px`,
});

const Img = styled("img", {
  height: "100%",
  width: "100%",
  padding: "$1",
  objectFit: "contain",

  variants: {
    blend: {
      true: {
        mixBlendMode: "multiply",
      },
      false: {},
    },
  },
});
const GoButton = styled(Button, {
  padding: "0",
  height: "$6",
  display: "grid",
  marginLeft: ".5rem",
  marginRight: ".5rem",
  placeItems: "center",
  justifySelf: "start",
  userSelect: "none",
});

const TypesButton = styled(Button, {
  padding: "0",
  height: "$6",
  display: "grid",
  marginLeft: "2rem",
  marginRight: ".5rem",
  placeItems: "center",
  justifySelf: "start",
  userSelect: "none",
  position: "absolute",
  top: 0,
  right: "$1",
});

const SearchControls = styled("div", {
  display: "grid",
  gridTemplateColumns: " 1fr  auto ",
  gridGap: "$2",
  overflow: "hidden",
  padding: "$2",
  alignItems: "center",
  height: `${CONTROL_HEIGHT}px`,
});

const BrowserContainer = styled("div", {
  display: "grid",
  gridTemplateRows: "48px 1fr",
  overflow: "hidden",
});
